import React from "react";
import { useCart } from "../context/CartContext";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { Helmet } from "react-helmet-async";

function Cart() {
  const phoneNumber = "254724835785"; // Replace with your WhatsApp number
  const { cartItems, removeFromCart, updateQuantity } = useCart();

  const subtotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0,
  );

  let message = "Hi, I'm interested in the following products:\n\n";
  if (cartItems.length > 0) {
    message += cartItems
      .map(
        (item, index) =>
          `${index + 1}. *${item.name}*\n📦 Qty: ${
            item.quantity
          }\n💰 Price: Ksh ${item.price.toFixed(2)}\n🖼️ Image: ${item.image}\n`,
      )
      .join("\n");
    message += `\n\n🧾 *Subtotal:* Ksh ${subtotal.toFixed(
      2,
    )}\n\nPlease provide more details.`;
  } else {
    message =
      "Hi, I would like to inquire about some products, but my cart seems empty.";
  }

  const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
    message,
  )}`;

  return (
    <div className="bg-white min-h-screen pt-20 pb-20">
      <Helmet>
        <title>Your Cart | Pak Fashions</title>
        <meta
          name="description"
          content="Review your selected fashion items in the cart. Adjust quantities, remove items, or proceed to checkout or WhatsApp booking with ease."
        />
        <meta
          name="keywords"
          content="fashion cart, shopping cart, pak fashions cart, buy clothes online Kenya, fashion checkout"
        />
        <meta name="author" content="Pak Fashions" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph for Facebook, LinkedIn, etc. */}
        <meta property="og:title" content="Your Cart | Pak Fashions" />
        <meta
          property="og:description"
          content="Explore the items you've added to your Pak Fashions cart. Continue shopping or place your order via WhatsApp."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pakfashions.co.ke/cart" />
        <meta
          property="og:image"
          content="https://pakfashions.co.ke/pak-circle.png"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Your Cart | Pak Fashions" />
        <meta
          name="twitter:description"
          content="Review your cart items and place your fashion order from Pak Fashions."
        />
        <meta
          name="twitter:image"
          content="https://pakfashions.co.ke/pak-circle.png"
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Cart Items */}
        <section className="lg:col-span-2 space-y-8 mt-10">
          <header className="border-b border-gray-100 pb-6 flex items-end justify-between">
            <div>
              <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">
                Your Cart
              </h2>
              <p className="text-gray-500 mt-2 font-medium">
                {cartItems.length} {cartItems.length === 1 ? "item" : "items"}{" "}
                in your bag
              </p>
            </div>
          </header>

          {cartItems.length === 0 ? (
            <div className="text-center py-20 bg-gray-50 rounded-3xl border border-gray-100 shadow-sm">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Your cart is empty
              </h3>
              <p className="text-gray-500 mb-8 font-medium">
                Looks like you haven't added anything yet.
              </p>
              <Link to="/shop">
                <button className="bg-yellow-500 hover:bg-yellow-400 text-black px-10 py-4 rounded-full font-bold shadow-md hover:-translate-y-1 transition-all">
                  Continue Shopping
                </button>
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {cartItems.map((item) => (
                <article
                  key={item.id}
                  className="flex flex-col sm:flex-row items-center bg-white border border-gray-100 rounded-3xl shadow-sm hover:shadow-lg transition-all p-5 gap-6 group"
                >
                  <Link to={`/product/${item.id}`} className="shrink-0">
                    <div className="relative overflow-hidden rounded-2xl bg-gray-50">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full sm:w-36 h-40 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </Link>
                  <div className="flex flex-col justify-between flex-grow w-full py-1">
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <Link to={`/product/${item.id}`}>
                          <h3 className="text-xl font-bold text-gray-900 hover:text-yellow-600 transition-colors line-clamp-1">
                            {item.name}
                          </h3>
                        </Link>
                        <p className="text-gray-500 mt-1 text-sm line-clamp-1">
                          {item.description}
                        </p>
                      </div>
                      <p className="text-xl font-extrabold text-black shrink-0">
                        Ksh {item.price.toLocaleString()}
                      </p>
                    </div>

                    <div className="flex items-center justify-between mt-6">
                      {/* Quantity Controls */}
                      <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          disabled={item.quantity <= 1}
                          className="px-4 py-2 text-gray-500 hover:bg-gray-50 hover:text-yellow-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        >
                          <Minus size={18} />
                        </button>
                        <span className="w-10 text-center font-bold text-gray-900 bg-gray-50/50 py-2">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="px-4 py-2 text-gray-500 hover:bg-gray-50 hover:text-yellow-600 transition-colors"
                        >
                          <Plus size={18} />
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="flex items-center gap-2 text-gray-400 hover:text-red-500 hover:bg-red-50 px-4 py-2 rounded-xl transition-all"
                        title="Remove from Cart"
                      >
                        <Trash2 size={20} />{" "}
                        <span className="hidden sm:inline font-medium">
                          Remove
                        </span>
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Order Summary */}
        <aside className="mt-10 lg:mt-32">
          <div className="bg-gray-900 text-white rounded-3xl p-8 sticky top-28 shadow-2xl">
            <h3 className="text-2xl font-bold mb-8 tracking-tight">
              Order Summary
            </h3>

            <div className="space-y-4 mb-8">
              <div className="flex justify-between text-gray-400 font-medium">
                <span>Subtotal</span>
                <span className="text-white">
                  Ksh {subtotal.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-gray-400 font-medium">
                <span>Shipping</span>
                <span className="text-yellow-400">Calculated later</span>
              </div>

              <div className="border-t border-gray-700 pt-4 mt-6">
                <div className="flex justify-between items-end">
                  <span className="text-lg font-bold">Total</span>
                  <span className="text-3xl font-extrabold text-yellow-500">
                    Ksh {subtotal.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* <Link to="/checkout" className="w-full">
                <button 
                  disabled={cartItems.length === 0}
                  className="w-full py-4 bg-white hover:bg-gray-100 text-black font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Proceed to Checkout
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </Link> */}

              {/* <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-gray-700"></div>
                <span className="flex-shrink-0 mx-4 text-gray-500 text-sm font-medium">OR</span>
                <div className="flex-grow border-t border-gray-700"></div>
              </div> */}

              <a
                href={whatsappURL}
                target="_blank"
                rel="noopener noreferrer"
                disabled={cartItems.length === 0}
                className={`w-full py-4 bg-[#25D366] hover:bg-[#1ebd5a] text-white font-bold rounded-xl shadow-md transition-all flex items-center justify-center gap-2 ${cartItems.length === 0 ? "opacity-50 pointer-events-none" : ""}`}
              >
                <FaWhatsapp className="text-2xl" />
                Book via WhatsApp
              </a>
            </div>

            <p className="text-center text-xs text-gray-500 mt-6 font-medium">
              Secure checkout. Free returns within 3 days.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default Cart;
